import React, { useState, useEffect } from 'react';

/**
 * Компонент аналоговых часов
 * @param {Object} props
 * @param {number} props.size - Размер часов в пикселях
 * @param {string} props.timezone - Часовой пояс (по умолчанию - локальный пользователя)
 */
const AnalogClock = ({ size = 200, timezone = null }) => {
  const [time, setTime] = useState(new Date());
  
  // Обновление времени каждую секунду
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Получаем время в заданном часовом поясе, если он указан
  const getTimeInTimezone = () => {
    if (!timezone) return time;
    
    try {
      // Получаем текущее время в формате ISO
      const now = new Date();
      
      // Конвертируем время в указанный часовой пояс
      const options = { timeZone: timezone, hour12: false };
      const timeString = now.toLocaleString('en-US', options);
      
      return new Date(timeString);
    } catch (error) {
      console.error('Ошибка при конвертации времени для часового пояса:', error);
      return time; // Возвращаем локальное время в случае ошибки
    }
  };
  
  // Расчет углов для стрелок часов
  const getClockAngles = () => {
    const currentTime = getTimeInTimezone();
    const hours = currentTime.getHours() % 12;
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();
    
    // Угол для часовой стрелки (30 градусов в час + дополнительное смещение от минут)
    const hourAngle = (hours * 30) + (minutes * 0.5);
    
    // Угол для минутной стрелки (6 градусов в минуту)
    const minuteAngle = minutes * 6;
    
    // Угол для секундной стрелки (6 градусов в секунду)
    const secondAngle = seconds * 6;
    
    return { hourAngle, minuteAngle, secondAngle };
  };
  
  const { hourAngle, minuteAngle, secondAngle } = getClockAngles();
  
  // Размеры и позиционирование стрелок часов
  const clockCenter = size / 2;
  const hourHandLength = size * 0.25;
  const minuteHandLength = size * 0.35;
  const secondHandLength = size * 0.4;
  
  // Толщина стрелок
  const hourHandWidth = size * 0.04;
  const minuteHandWidth = size * 0.03;
  const secondHandWidth = size * 0.02;
  
  // Радиус циферблата
  const clockRadius = size / 2 - 5;
  
  // Генерация точек для часовых меток
  const hourMarkers = Array.from({ length: 12 }, (_, i) => {
    const angle = i * 30 * (Math.PI / 180); // преобразуем в радианы
    const markerLength = i % 3 === 0 ? 10 : 5; // Каждые 3 часа длиннее метка
    const innerRadius = clockRadius - markerLength;
    
    return {
      x1: clockCenter + innerRadius * Math.sin(angle),
      y1: clockCenter - innerRadius * Math.cos(angle),
      x2: clockCenter + clockRadius * Math.sin(angle),
      y2: clockCenter - clockRadius * Math.cos(angle)
    };
  });
  
  return (
    <div className="relative inline-block">
      <svg 
        width={size} 
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="rounded-full bg-white dark:bg-gray-800 border-4 border-gray-300 dark:border-gray-600 shadow"
      >
        {/* Циферблат */}
        <circle 
          cx={clockCenter} 
          cy={clockCenter} 
          r={clockRadius} 
          fill="white" 
          className="dark:fill-gray-700 dark:stroke-gray-600"
          stroke="#CBD5E0" 
          strokeWidth="2"
        />
        
        {/* Часовые метки */}
        {hourMarkers.map((marker, index) => (
          <line
            key={index}
            x1={marker.x1}
            y1={marker.y1}
            x2={marker.x2}
            y2={marker.y2}
            stroke="#4A5568"
            className="dark:stroke-gray-400"
            strokeWidth={index % 3 === 0 ? 3 : 2}
            strokeLinecap="round"
          />
        ))}
        
        {/* Числа на циферблате */}
        {Array.from({ length: 12 }, (_, i) => {
          const hour = i === 0 ? 12 : i;
          const angle = i * 30 * (Math.PI / 180); // преобразуем в радианы
          const numberRadius = clockRadius - 25;
          const x = clockCenter + numberRadius * Math.sin(angle);
          const y = clockCenter - numberRadius * Math.cos(angle);
          
          return (
            <text
              key={i}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={size * 0.08}
              fontWeight="bold"
              fill="#2D3748"
              className="dark:fill-gray-300"
            >
              {hour}
            </text>
          );
        })}
        
        {/* Центральная точка */}
        <circle 
          cx={clockCenter} 
          cy={clockCenter} 
          r={size * 0.025} 
          fill="#4A5568"
          className="dark:fill-gray-400" 
        />
        
        {/* Часовая стрелка */}
        <line 
          x1={clockCenter} 
          y1={clockCenter} 
          x2={clockCenter + hourHandLength * Math.sin(hourAngle * (Math.PI / 180))} 
          y2={clockCenter - hourHandLength * Math.cos(hourAngle * (Math.PI / 180))} 
          stroke="#2D3748" 
          className="dark:stroke-gray-300"
          strokeWidth={hourHandWidth} 
          strokeLinecap="round" 
        />
        
        {/* Минутная стрелка */}
        <line 
          x1={clockCenter} 
          y1={clockCenter} 
          x2={clockCenter + minuteHandLength * Math.sin(minuteAngle * (Math.PI / 180))} 
          y2={clockCenter - minuteHandLength * Math.cos(minuteAngle * (Math.PI / 180))} 
          stroke="#4A5568" 
          className="dark:stroke-gray-400"
          strokeWidth={minuteHandWidth} 
          strokeLinecap="round" 
        />
        
        {/* Секундная стрелка */}
        <line 
          x1={clockCenter} 
          y1={clockCenter} 
          x2={clockCenter + secondHandLength * Math.sin(secondAngle * (Math.PI / 180))} 
          y2={clockCenter - secondHandLength * Math.cos(secondAngle * (Math.PI / 180))} 
          stroke="#E53E3E" 
          className="dark:stroke-red-500"
          strokeWidth={secondHandWidth} 
          strokeLinecap="round" 
        />
        
        {/* Накладка на центральную точку (чтобы стрелки выглядели наложенными) */}
        <circle 
          cx={clockCenter} 
          cy={clockCenter} 
          r={size * 0.015} 
          fill="#E53E3E"
          className="dark:fill-red-500" 
        />
      </svg>
    </div>
  );
};

export default AnalogClock;